import { supabase } from "../lib/supabase";
import type { DeadlineCategory } from "../constants/calendar";

// ========== TYPES ==========

export interface StudentDeadline {
  id: string;
  student_id: string;
  title: string;
  description?: string;
  deadline_date: string; // ISO date string
  category: DeadlineCategory;
  created_at: string;
  updated_at: string;
}

export interface CollegeDeadline {
  id: number;
  college_id: number;
  college: string;
  early_type: string | null;
  early_deadline: string | null;
  regular_deadline: string | null;
  created_at?: string;
}

export interface CreateDeadlineData {
  title: string;
  description?: string;
  deadline_date: string;
  category: DeadlineCategory;
}

export interface UpdateDeadlineData {
  title?: string;
  description?: string;
  deadline_date?: string;
  category?: DeadlineCategory;
}

async function requireUserId(): Promise<string> {
  // Prefer session (local) to avoid a network roundtrip / transient getUser issues.
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const sessionUserId = session?.user?.id;
  if (sessionUserId) return sessionUserId;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  const userId = user?.id;
  if (userId) return userId;

  throw new Error("Authentication required");
}

// ========== STUDENT DEADLINES ==========

export async function getStudentDeadlines(): Promise<StudentDeadline[]> {
  const userId = await requireUserId();

  const { data, error } = await supabase
    .from("student_deadlines")
    .select("*")
    .eq("student_id", userId)
    .order("deadline_date", { ascending: true });

  if (error) throw new Error(error.message);
  return data as StudentDeadline[];
}

export async function createDeadline(
  deadlineData: CreateDeadlineData
): Promise<StudentDeadline> {
  const userId = await requireUserId();

  const { data, error } = await supabase
    .from("student_deadlines")
    .insert({
      ...deadlineData,
      student_id: userId,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as StudentDeadline;
}

export async function updateDeadline(
  id: string,
  updates: UpdateDeadlineData
): Promise<StudentDeadline> {
  const userId = await requireUserId();

  const { data, error } = await supabase
    .from("student_deadlines")
    .update(updates)
    .eq("id", id)
    .eq("student_id", userId)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as StudentDeadline;
}

export async function deleteDeadline(id: string): Promise<void> {
  const userId = await requireUserId();

  const { error } = await supabase
    .from("student_deadlines")
    .delete()
    .eq("id", id)
    .eq("student_id", userId);

  if (error) throw new Error(error.message);
}

// ========== COLLEGE DEADLINES ==========

/**
 * Get college deadlines by college IDs (more efficient than name matching)
 * @param collegeIds - Array of college IDs (csc_unitid from location_type_of_institution)
 */
export async function getCollegeDeadlines(collegeIds: number[]): Promise<CollegeDeadline[]> {
  if (collegeIds.length === 0) return [];

  const { data, error } = await supabase
    .from('deadlines')
    .select('*')
    .in('college_id', collegeIds);

  if (error) throw new Error(error.message);

  return (data || []) as CollegeDeadline[];
}

/**
 * @deprecated Use getCollegeDeadlines(collegeIds: number[]) instead
 * Legacy function for backwards compatibility - matches by college name
 */
export async function getCollegeDeadlinesByName(collegeNames: string[]): Promise<CollegeDeadline[]> {
  if (collegeNames.length === 0) return [];

  const { data, error } = await supabase
    .from('deadlines')
    .select('*')
    .in('college', collegeNames);

  if (error) throw new Error(error.message);

  return (data || []) as CollegeDeadline[];
}
